import Button from "../../../custom/button/button";
import { ReactComponent as Edit } from "../../../assets/Edit.svg";
import { ReactComponent as Document } from "../../../assets/Group (5).svg";
import { ReactComponent as DownloadPdf } from "../../../assets/Export Pdf.svg";
import { ReactComponent as Cancel } from "../../../assets/cancel.svg";
import PageLayout from "../../../layouts/pageLayout/pageLayout";
import styles from "../dashboardPage.module.scss";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import api from "../../../utils/api";
import { App, Modal, Spin } from "antd";
import { AxiosError } from "axios";
import { useState } from "react";
import QualificationForm from "./form";
import { useAtomValue } from "jotai";
import { userAtom } from "../../../utils/store";
import { FormikValues } from "formik";

interface QualificationResponse {
  data: Qualification;
}

interface FormSubject {
  subject: string;
  grade: string;
}

const documentName = (str: string) =>
  !!str ? str.slice(str.lastIndexOf("/") + 1) : str;

const QualificationView = () => {
  const { notification } = App.useApp();
  const { id: qualificationId } = useParams();
  const user = useAtomValue(userAtom);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const createOrUpdateQualificationMutation = useMutation({
    mutationFn: async (payload: FormData) => {
      return (await api.post(
        "/StudentInfo/StudentInfo/CreateUpdateQualification",
        payload
      )) as Response;
    },
  });
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["get-qualification-by-id"],
    queryFn: async () => {
      return (
        await api.get(
          `/StudentInfo/StudentInfo/GetQualificationById?Id=${qualificationId}`
        )
      )?.data as QualificationResponse;
    },
    enabled: !!qualificationId,
  });

  const handleCreateorUpdateQualification = async (
    values: FormikValues,
    resetForm: () => void
  ) => {
    const payload = new FormData();
    try {
      payload.append("Id", qualificationId!);
      payload.append("ApplicantId", user?.applicantId ?? '');
      payload.append("QualificationType", values.qualificationType);
      payload.append("ExamNumber", values.examNumber);
      if (values.certificateUrl) {
        payload.append("Certificate", values.certificate);
      }
      payload.append("CertificateiUrl", "");
      values.subjects?.forEach((subject: FormSubject, index: number) => {
        payload.append(`Subjects[${index}].subject`, subject.subject);
        payload.append(`Subjects[${index}].grade`, subject.grade);
        payload.append(`Subjects[${index}].activeStatus`, "true");
        payload.append(`Subjects[${index}].isDeleted`, "false");
      });
      payload.append("isDeleted", "false");
      payload.append("IsSubmmited", "true");
      await createOrUpdateQualificationMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });
          refetch();
          setShowModal(false);
          setIsEditing(false);
          resetForm();
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.message || error?.response?.data?.message,
      });
    }
  };

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (isError) {
    return <div>{(error as AxiosError)?.message}</div>;
  }

  const qualification = data?.data as Qualification;

  return (
    <PageLayout
      heading="Qualifications"
      headerActions={
        <>
          <Button
            iconBefore={<Edit />}
            onClick={() => {
              setIsEditing(true);
              setShowModal(true);
            }}
            text="Edit"
          />
          <Button
            iconBefore={<DownloadPdf />}
            text="Download"
            bgColor="#CE9938"
          />
        </>
      }
    >
      <section className={styles?.section}>
        <p>Qualification Type: {qualification?.qualificationType}</p>
        <p>Exam Number: {qualification?.examNumber}</p>

        <section className={styles.textTable}>
          <div>Result: </div>
          <div>
            {qualification?.subjects?.map((subject) => (
              <p key={subject.id} className={styles.subjectCol}>
                {subject?.subject} - {subject?.grade}
              </p>
            ))}
          </div>
        </section>
      </section>

      <section className={styles.docTable}>
        <div className={styles.docRow}>
          <div className={styles.docCell}>
            <p className={styles.title}>{document.title}</p>
            <Document className={styles.doc} />
            <p className={styles.doc}>
              {documentName(qualification?.certificateiUrl)}
            </p>
          </div>
        </div>
      </section>
      <Modal
        open={showModal}
        footer=""
        onCancel={() => setShowModal(false)}
        centered
        closeIcon={<Cancel />}
        width={900}
      >
        <div className={styles.modalContent}>
          <div>
            <p>Qualification</p>
            <p>All fields marked with a (*) are required.</p>
          </div>

          <hr />

          <br />
          <QualificationForm
            qualification={qualification}
            isEditing={isEditing}
            isLoading={createOrUpdateQualificationMutation.isPending}
            onSubmit={handleCreateorUpdateQualification}
          />
        </div>
      </Modal>
    </PageLayout>
  );
};

export default QualificationView;
